import React, { useState, useEffect } from 'react';
import MuiAlert from '@mui/material/Alert';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { getTimeZones } from '@vvo/tzdb';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { StoreDeviceConfiguration } from 'components/admin/stores/deviceConfiguration/StoreDeviceConfiguration';
import { PayPalAccount } from 'components/admin/stores/paypalAccount/PayPalAccount';
import { TerminalAccounts } from 'components/admin/stores/payrocTerminals/TerminalAccounts';
import Modal from 'components/common/Modal';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { CentsDataGridServer } from 'components/common/dataGrid/dataGridServer';
// Components
import DetailsCard from 'components/common/detailsCard/DetailsCard';
import IndividualValueEditForm from 'components/common/forms/IndividualValueEditForm';
import TableSearchBar from 'components/common/search/TableSearchBar';
import Tabs from 'components/common/tabsTable/TabsTable';
import { DocumentUpload } from 'components/common/upload/DocumentUpload';
// Constants
import { onPremiseConnectivityMethods, onPremisePaymentProviders } from 'utils/constants';
import passwordValidation from 'utils/passwordValidation';
// API
import { getIndividualStore } from 'api/admin/stores/getIndividualStore';
import {
  updateIndividualValueForStore,
  updateIndividualValueForStoreSettings,
  updateIndividualValueForCentsDeliverySettings,
  updateValueForPrinterSettings,
  updateStorePassword,
  getStoreServiceOrders,
  getStoreInventoryOrders,
} from 'api/admin/stores/stores';
import { useUploadOfflineMachinesForStore } from 'api/queries/useUploadOfflineMachinesForStore';
import { DELIVERY_PREFERENCES, StoreSettings } from 'constants/store/storeSettings';

const TABLE_TAB_VALUES = {
  serviceOrders: 0,
  inventoryOrders: 1,
  payrocTerminalAccounts: 2,
  paypalAccount: 3,
  machineUploads: 4,
  deviceConfiguration: 5,
};

const MODAL_TYPES = {
  ...StoreSettings,
};

const IndividualStoreProfile = () => {
  const { id } = useParams();

  const [store, setStore] = useState({});
  const [errorMessage, setErrorMessage] = useState();
  const [toggleErrorMessage, setToggleErrorMessage] = useState();
  const [operationalStats, setOperationalStats] = useState({});
  const [defaultReleases, setDefaultReleases] = useState([]);
  const [storeSettings, setStoreSettings] = useState({});
  const [printerSettings, setPrinterSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState();
  const [showModal, setShowModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showCentsDeliverySettingsModal, setShowCentsDeliverySettingsModal] = useState(false);
  const [showPrinterSettingsModal, setShowPrinterSettingsModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [modalType, setModalType] = useState(false);
  const [updatedFormValue, setUpdatedFormValue] = useState(null);
  const [updatedFormKey, setUpdatedFormKey] = useState(null);
  const [updatedStoreSettingsValue, setUpdatedStoreSettingsValue] = useState(null);
  const [updatedPrinterSettingsValue, setUpdatedPrinterSettingsValue] = useState(null);
  const [updatedCentsDeliverySettingsValue, setUpdatedCentsDeliverySettingsValue] = useState(null);
  const [timeZones, setTimeZones] = useState([]);
  const [tabsData, setTabsData] = useState([]);
  const [tableTabsData, setTableTabsData] = useState([]);
  const [tabValueSelected, setTabValueSelected] = useState(0);
  const [tableTabValueSelected, setTableTabValueSelected] = useState(
    TABLE_TAB_VALUES.serviceOrders,
  );
  const [inventoryOrdersPageNumber, setInventoryOrdersPageNumber] = useState(0);
  const [serviceOrdersPageNumber, setServiceOrdersPageNumber] = useState(0);
  const [inventoryOrdersSearchString, setInventoryOrdersSearchString] = useState('');
  const [serviceOrdersSearchString, setServiceOrdersSearchString] = useState('');
  const history = useHistory();

  const { data: serviceOrderData, isLoading: isServiceOrdersTableLoading } = useQuery({
    queryKey: [
      'searchServiceOrdersForStore',
      { serviceOrdersPageNumber, id, serviceOrdersSearchString },
    ],
    queryFn: async () => {
      const response = await getStoreServiceOrders(
        serviceOrdersPageNumber,
        id,
        serviceOrdersSearchString,
      );
      return {
        serviceOrders: response?.data.serviceOrders ?? [],
        serviceOrderCount: response?.data.total,
      };
    },
    onError: (error) => {
      console.log(error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
  });
  const { serviceOrders = [], serviceOrderCount = 0 } = serviceOrderData ?? {};

  const { data: inventoryOrderData, isLoading: isInventoryOrdersTableLoading } = useQuery({
    queryKey: [
      'searchInventoryOrdersForStore',
      { inventoryOrdersPageNumber, id, inventoryOrdersSearchString },
    ],
    queryFn: async () => {
      const response = await getStoreInventoryOrders(
        inventoryOrdersPageNumber,
        id,
        inventoryOrdersSearchString,
      );
      return {
        inventoryOrders: response?.data.inventoryOrders ?? [],
        inventoryOrderCount: response?.data.total,
      };
    },
    onError: (error) => {
      console.log(error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
  });
  const { inventoryOrders = [], inventoryOrderCount = 0 } = inventoryOrderData ?? {};

  const { isLoading: isUploadingOfflineMachinesForStore, mutate: uploadOfflineMachinesForStore } =
    useUploadOfflineMachinesForStore();

  const handleTabChange = (event, newValue) => {
    setTabValueSelected(newValue);
  };

  const handleTableTabChange = (event, newValue) => {
    setTableTabValueSelected(newValue);
  };

  const connectionTypes = [
    { display: 'Bluetooth', value: 'BLUETOOTH' },
    { display: 'Ethernet', value: 'ETHERNET' },
    { display: 'Wi-Fi', value: 'WIFI' },
  ];

  const brands = [
    { display: 'Epson', value: 'EPSON' },
    { display: 'Star Micronics', value: 'STARMICRONICS' },
  ];

  const tabletBrands = [
    { display: 'Samsung', value: 'SAMSUNG' },
    { display: 'Sunmi T2s', value: 'SUNMI_T2S' },
    { display: 'Sunmi T2s LITE', value: 'SUNMI_T2S_LITE' },
    { display: 'Sunmi D3 Pro', value: 'SUNMI_D3_PRO' },
    { display: 'Sunmi M2 Max', value: 'SUNMI_M2_MAX' },
    { display: 'Sunmi T3', value: 'SUNMI_T3' },
  ];

  /**
   * Retrieve the store details using the id params
   */
  const retrieveStoreDetails = async ({ showLoader = true } = {}) => {
    try {
      if (showLoader) {
        setLoading(true);
      }

      const storeData = await getIndividualStore(id);

      if (storeData) {
        const { store, stats, defaultReleases: storeDefaultReleases } = storeData.data;

        setStore(store);
        setStoreSettings(store.settings);
        setPrinterSettings(store.printerSettings);
        setOperationalStats(stats);
        setDefaultReleases(storeDefaultReleases ?? []);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.response?.data.error ?? error.message);
    }
  };

  /**
   * Format the timezone database list for our IndividualValueEditForm
   */
  const formatTimeZoneList = () => {
    let zones = getTimeZones();
    zones = zones.map((zone) => ({
      display: zone.name,
      value: zone.name,
    }));
    setTimeZones(zones);
  };

  useEffect(async () => {
    retrieveStoreDetails();
    formatTimeZoneList();
    // eslint-disable-next-line
  }, []);

  useEffect(async () => {
    if (store.id) {
      createTabsData();
      createTableTabsData();
    }
  }, [store]);

  useEffect(async () => {
    createTabsData();
  }, [storeSettings, printerSettings, operationalStats]);

  /**
   * Save a key-value pair to the Store model
   *
   * @param {String} field
   * @param {Any} value
   */
  const saveIndividualValueForStore = async (field, value) => {
    try {
      setLoading(true);
      setToggleErrorMessage(null);

      const data = {
        field,
        value,
      };

      if (typeof value === 'string') {
        let newValue = value.trim();
        if (!newValue) {
          newValue = null;
        }
        data.value = newValue;
      }

      const storeData = await updateIndividualValueForStore(store.id, data);
      setStore(storeData.data.store);
      setStoreSettings(storeData.data.store.settings);

      setShowModal(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setToggleErrorMessage(error.response.data.error);
    }
  };

  /**
   * Validate the parameters are not null when necessary for updating store settings
   *
   * @param {String} field
   * @param {Any} value
   */
  const validateStoreSettings = (field, value) => {
    if (
      (field === 'onPremisePaymentProvider' || field === 'onPremiseConnectivityMethod') &&
      !value
    ) {
      return false;
    }

    return true;
  };

  /**
   * Save a key-value pair to the StoreSettings model
   *
   * @param {String} field
   * @param {Any} value
   */
  const saveIndividualValueForStoreSettings = async (field, value) => {
    try {
      setLoading(true);
      setToggleErrorMessage(null);

      const isRequestValid = validateStoreSettings(field, value);

      if (!isRequestValid) {
        setLoading(false);
        return setError(
          'The item you are trying to change requires a value. Please ensure you have selected a value to choose from.',
        );
      }

      const data = {
        field,
        value,
      };

      const storeData = await updateIndividualValueForStoreSettings(store.id, data);
      setStore(storeData.data.store);
      setStoreSettings(storeData.data.store.settings);

      setShowSettingsModal(false);
      setUpdatedStoreSettingsValue(null);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setToggleErrorMessage(error.response.data.error);
    }
  };

  /**
   * Save a key-value pair to the StoreSettings model
   *
   * @param {String} field
   * @param {Any} value
   */
  const saveIndividualValueForPrinterSettings = async (field, value) => {
    try {
      setLoading(true);

      const data = {
        field,
        value,
      };

      const storeData = await updateValueForPrinterSettings(store.id, data);
      setStore(storeData.data.store);
      setStoreSettings(storeData.data.store.settings);
      setPrinterSettings(storeData.data.store.printerSettings);

      setShowPrinterSettingsModal(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.response.data.error);
    }
  };

  /**
   * Save a key-value pair to the Cents Delivery model
   *
   * @param {String} field
   * @param {Any} value
   */
  const saveIndividualValueForCentsDeliverySettings = async (field, value) => {
    try {
      setLoading(true);

      const data = {
        field,
        value,
      };

      const storeData = await updateIndividualValueForCentsDeliverySettings(store.id, data);
      setStore(storeData.data.store);
      setStoreSettings(storeData.data.store.settings);

      setShowCentsDeliverySettingsModal(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.response.data.error);
    }
  };

  /**
   * Set the form value to be updated for the Store
   *
   * @param {Any} value
   */
  const handleStoreValueChange = (value) => {
    setError(null);
    setUpdatedFormValue(value);
  };

  /**
   * Set the form value to be updated for the Store
   *
   * @param {Any} event
   */
  const handleStoreSettingsValueChange = (value) => {
    setError(null);
    setUpdatedStoreSettingsValue(value?.value || value);
  };

  /**
   * Set the form value to be updated for the PrinterStoreSettings model
   *
   * @param {Any} event
   */
  const handlePrinterSettingsValueChange = (event) => {
    setError(null);
    setUpdatedPrinterSettingsValue(event.value);
  };

  /**
   * Set the form value to be updated for the CentsDeliverySettings model
   *
   * @param {Any} event
   */
  const handleCentsDeliveryValueChange = (newValue) => {
    setError(null);
    setUpdatedCentsDeliverySettingsValue(newValue);
  };

  const openEditModal = (modalType) => {
    setModalType(modalType);
    setUpdatedFormKey(modalType);

    if (modalType === 'Password') {
      setShowPasswordModal(true);
    }
    if (modalType.includes('stripe') || modalType.includes('Stripe')) {
      setShowModal(true);
    }
    if (modalType === 'brand' || modalType === 'connectivityType') {
      setShowPrinterSettingsModal(true);
    }
    if (modalType === MODAL_TYPES.TABLET_BRAND || modalType === 'tabletSerialNumber') {
      setShowSettingsModal(true);
    }
    if (modalType === 'nashDispatchStrategyId') {
      setShowCentsDeliverySettingsModal(true);
    }
    if (
      modalType === MODAL_TYPES.TIME_ZONE ||
      modalType === MODAL_TYPES.TURN_AROUND_IN_HOURS ||
      modalType === MODAL_TYPES.ON_PREMISE_PAYMENT_PROVIDER ||
      modalType === MODAL_TYPES.ON_PREMISE_CONNECTIVITY_METHOD ||
      modalType === MODAL_TYPES.TRANSPORTATION_PREFERENCE
    ) {
      setShowSettingsModal(true);
    }
  };

  /**
   * Determine which function to map the toggle action
   *
   * @param {String} field
   * @param {Any} value
   */
  const handleStoreToggle = (field, value) => {
    switch (field) {
      case 'hasSmsEnabled':
      case 'deliveryEnabled':
      case 'autoScheduleReturnEnabled':
      case 'showRackDetailsInLiveLink':
      case 'rwgEnabled':
        return saveIndividualValueForStoreSettings(field, value);
      default:
        return saveIndividualValueForStore(field, value);
    }
  };

  /**
   * Render the core store details
   */
  const renderStoreDetails = () => {
    const storeDetails = [
      {
        label: 'Store Name',
        data: store.name,
      },
      {
        label: 'Address',
        data: store.address,
      },
      {
        label: 'City',
        data: store.city,
      },
      {
        label: 'State',
        data: store.state,
      },
      {
        label: 'Zip Code',
        data: store.zipCode,
      },
      {
        label: 'Business',
        data: store?.laundromatBusiness?.name,
      },
      {
        label: 'Password',
        data: 'Reset Password',
        editable: true,
      },
      {
        label: 'Delivery',
        data: storeSettings.deliveryEnabled,
        rawLabel: 'deliveryEnabled',
        toggleable: true,
      },
      {
        label: 'Laundroworks Integration',
        data: store.hasLaundroworksEnabled ? 'Yes' : 'No',
      },
      {
        label: 'Stripe Location Id',
        rawLabel: 'stripeLocationId',
        data: store.stripeLocationId,
        editable: true,
      },
      {
        label: 'Stripe Terminal Id',
        rawLabel: 'stripeTerminalId',
        data: store.stripeTerminalId,
        editable: true,
        underlineBorder: true,
      },
      {
        label: 'Time Zone',
        rawLabel: 'timeZone',
        data: storeSettings.timeZone,
        editable: true,
      },
      {
        label: 'Enable SMS',
        data: storeSettings.hasSmsEnabled,
        rawLabel: 'hasSmsEnabled',
        toggleable: true,
      },
      {
        label: 'Delivery Preference',
        data: storeSettings?.transportationPreference,
        rawLabel: StoreSettings.TRANSPORTATION_PREFERENCE,
        editable: true,
      },
      {
        label: 'Show rack in live link',
        rawLabel: 'showRackDetailsInLiveLink',
        data: storeSettings?.showRackDetailsInLiveLink,
        toggleable: true,
      },
      {
        label: 'Nash Dispatch Strategy',
        data: store.centsDelivery?.nashDispatchStrategyId,
        rawLabel: 'nashDispatchStrategyId',
        editable: true,
      },
      {
        label: 'Reserve with Google',
        data: storeSettings.rwgEnabled,
        rawLabel: 'rwgEnabled',
        toggleable: true,
      },
      {
        label: 'Tablet Brand',
        rawLabel: StoreSettings.TABLET_BRAND,
        data: storeSettings?.tabletBrand,
        editable: true,
        type: 'select',
      },
      {
        label: 'Tablet Serial Number',
        data: storeSettings?.tabletSerialNumber,
        rawLabel: 'tabletSerialNumber',
        editable: true,
      },
    ];

    return (
      <DetailsCard
        itemsPerColumn={7}
        cardData={storeDetails}
        onEdit={(modalType) => {
          openEditModal(modalType);
        }}
        onToggle={(field, value) => {
          handleStoreToggle(field, value);
        }}
      />
    );
  };

  /**
   * Show the edit-details modal
   */
  const renderModal = () => {
    const currentValue = store[modalType];

    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowModal(false);
          setUpdatedFormKey(null);
          setUpdatedFormValue(null);
        }}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={currentValue}
          updatedValue={updatedFormValue}
          handleValueChange={(event) => {
            handleStoreValueChange(event);
          }}
          inputType="text"
          onSave={() => {
            saveIndividualValueForStore(updatedFormKey, updatedFormValue);
          }}
          error={error}
        />
      </Modal>
    );
  };

  /**
   * Retrieve the proper input type for the StoreSettings editablity modal
   */
  const retrieveStoreSettingsInputType = () => {
    switch (modalType) {
      case MODAL_TYPES.TIME_ZONE:
      case MODAL_TYPES.ON_PREMISE_PAYMENT_PROVIDER:
      case MODAL_TYPES.ON_PREMISE_CONNECTIVITY_METHOD:
      case MODAL_TYPES.TRANSPORTATION_PREFERENCE:
        return 'select';
      case MODAL_TYPES.TABLET_BRAND:
        return 'select';
      default:
        return 'text';
    }
  };

  /**
   * Retrieve the proper select options for the StoreSettings editablity modal
   */
  const retrieveStoreSettingsSelectOptions = () => {
    switch (modalType) {
      case MODAL_TYPES.TIME_ZONE:
        return timeZones;
      case MODAL_TYPES.ON_PREMISE_PAYMENT_PROVIDER:
        return onPremisePaymentProviders;
      case MODAL_TYPES.ON_PREMISE_CONNECTIVITY_METHOD:
        return onPremiseConnectivityMethods;
      case MODAL_TYPES.TRANSPORTATION_PREFERENCE:
        return DELIVERY_PREFERENCES;
      case MODAL_TYPES.TABLET_BRAND:
        return tabletBrands;
      default:
        return null;
    }
  };

  /**
   * Show the modal to edit store settings details
   */
  const renderStoreSettingsModal = () => {
    const currentValue = storeSettings[modalType];

    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowSettingsModal(false);
          setUpdatedFormKey(null);
          setUpdatedStoreSettingsValue(null);
          setError(null);
        }}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={currentValue}
          updatedValue={updatedStoreSettingsValue}
          handleValueChange={(value) => {
            handleStoreSettingsValueChange(value);
          }}
          inputType={retrieveStoreSettingsInputType()}
          selectOptions={retrieveStoreSettingsSelectOptions()}
          onSave={() => {
            saveIndividualValueForStoreSettings(updatedFormKey, updatedStoreSettingsValue);
          }}
          error={error}
        />
      </Modal>
    );
  };

  /**
   * Clear any errors and save changes to password in state
   */
  const handlePasswordChange = (password) => {
    setErrorMessage(null);
    setNewPassword(password);
  };

  /**
   * Set and save the new password for the user
   */
  const saveStorePassword = async () => {
    const validation = passwordValidation(newPassword);

    if (!validation.result) {
      setErrorMessage(validation.errorMessage);
      return;
    }

    try {
      setLoading(true);

      const data = {
        password: newPassword,
      };

      const updatedStore = await updateStorePassword(store.id, data);
      setStore(updatedStore.data.store);
      setModalType(null);
      setShowPasswordModal(false);
      setLoading(false);
    } catch (error) {
      setErrorMessage(error.response.data.error);
      setLoading(false);
    }
  };

  /**
   * Show the edit password modal
   */
  const renderPasswordModal = () => {
    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowPasswordModal(false);
          setErrorMessage(null);
        }}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={'********'}
          updatedValue={newPassword}
          handleValueChange={handlePasswordChange}
          inputType="text"
          onSave={saveStorePassword}
          error={errorMessage}
        />
      </Modal>
    );
  };

  /**
   * Show the modal to edit printer settings details
   */
  const renderPrinterSettingsModal = () => {
    const currentValue = printerSettings[modalType];

    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowPrinterSettingsModal(false);
          setUpdatedFormKey(null);
          setUpdatedPrinterSettingsValue(null);
        }}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={currentValue}
          updatedValue={updatedPrinterSettingsValue}
          handleValueChange={(event) => {
            handlePrinterSettingsValueChange(event);
          }}
          inputType={'select'}
          selectOptions={modalType === 'brand' ? brands : connectionTypes}
          onSave={() => {
            saveIndividualValueForPrinterSettings(updatedFormKey, updatedPrinterSettingsValue);
          }}
          error={error}
        />
      </Modal>
    );
  };

  /**
   * Show the modal to edit cents delivery settings details
   */
  const renderCentsDeliverySettingsModal = () => {
    const currentValue = store.centsDelivery?.[modalType];

    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowCentsDeliverySettingsModal(false);
          setUpdatedFormKey(null);
          setUpdatedCentsDeliverySettingsValue(null);
        }}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={currentValue}
          updatedValue={updatedCentsDeliverySettingsValue}
          handleValueChange={handleCentsDeliveryValueChange}
          inputType="text"
          onSave={() => {
            saveIndividualValueForCentsDeliverySettings(
              updatedFormKey,
              updatedCentsDeliverySettingsValue,
            );
          }}
          error={error}
        />
      </Modal>
    );
  };

  /**
   * Render the operational stats card
   */
  const renderOperationalStats = () => {
    const statsData = [
      {
        label: 'First Transaction Date',
        data: operationalStats.firstTransactionDate,
        rawLabel: 'firstTransactionDate',
      },
      {
        label: 'Latest Transaction Date',
        data: operationalStats.latestTransactionDate,
        rawLabel: 'latestTransactionDate',
      },
      {
        label: 'Total Online Transactions',
        data: Number(operationalStats.totalOnlineTransactions).toFixed(2),
        rawLabel: 'totalOnlineTransactions',
        currency: true,
      },
      {
        label: 'Total Walk-In Transactions',
        data: Number(operationalStats.totalWalkInTransactions).toFixed(2),
        rawLabel: 'totalWalkInTransactions',
        currency: true,
      },
    ];

    return <DetailsCard cardData={statsData} />;
  };

  const createTabsData = () => {
    const tabsDataArray = [
      { name: 'Store Details', value: 'storeDetails', children: renderStoreDetails() },
    ];

    let addOperationalStatsTab = false;

    Object.keys(operationalStats).forEach(function (key) {
      if (operationalStats[key]) addOperationalStatsTab = true;
    });

    if (addOperationalStatsTab) {
      tabsDataArray.push({
        name: 'Operational Stats',
        value: 'operationalStats',
        children: renderOperationalStats(),
      });
    }

    setTabsData(tabsDataArray);
  };

  const createInventoryOrdersTabsData = () => {
    const INVENTORY_ORDER_TABLE_COLUMNS = [
      { headerName: 'Store Name', field: 'storeName', flex: 1, sortable: false },
      { headerName: 'Business Name', field: 'businessName', flex: 1, sortable: false },
      { headerName: 'Customer Name', field: 'customerName', flex: 1, sortable: false },
      { headerName: 'Order Number', field: 'orderCode', flex: 1, sortable: false },
      { headerName: 'Order ID', field: 'id', flex: 1, sortable: false },
      { headerName: 'Status', field: 'status', flex: 1, sortable: false },
      { headerName: 'Order Total', field: 'orderTotal', flex: 1, sortable: false },
      { headerName: 'Order creation date', field: 'createdAt', flex: 1, sortable: false },
      { headerName: 'Payment Date', field: 'updatedAt', flex: 1, sortable: false },
      { headerName: 'Payment Method', field: 'paymentMethod', flex: 1, sortable: false },
      { headerName: 'Payment Status', field: 'paymentStatus', flex: 1, sortable: false },
      { headerName: 'Tip Amount', field: 'tipAmount', flex: 1, sortable: false },
      { headerName: 'Credit Amount', field: 'creditAmount', flex: 1, sortable: false },
      { headerName: 'Promotion Amount', field: 'promotionAmount', flex: 1, sortable: false },
    ];

    return (
      <CentsDataGridServer
        tableName="STORE_INVENTORY_ORDERS"
        columns={INVENTORY_ORDER_TABLE_COLUMNS}
        data={inventoryOrders}
        rowCount={inventoryOrderCount}
        pageSize={20}
        page={inventoryOrdersPageNumber}
        onPageChange={({ page }) => setInventoryOrdersPageNumber(page)}
        onRowClick={({ id }) => {
          navigateToIndividualInventoryOrder(id);
        }}
        isLoading={isInventoryOrdersTableLoading}
      />
    );
  };

  const createServiceOrdersTabsData = () => {
    const SERVICE_ORDER_TABLE_COLUMNS = [
      { headerName: 'ID', field: 'id', flex: 1, sortable: false },
      { headerName: 'Order Code', field: 'orderCode', flex: 1, sortable: false },
      { headerName: 'Store Name', field: 'storeName', flex: 1, sortable: false },
      { headerName: 'Customer Name', field: 'customerName', flex: 1, sortable: false },
      { headerName: 'Status', field: 'status', flex: 1, sortable: false },
      { headerName: 'Order Total', field: 'orderTotal', flex: 1, sortable: false },
      { headerName: 'Net Order Total', field: 'netOrderTotal', flex: 1, sortable: false },
      { headerName: 'Placed At', field: 'placedAt', flex: 1, sortable: false },
      { headerName: 'Due Date', field: 'dueDate', flex: 1, sortable: false },
      { headerName: 'Completed At', field: 'completedAt', flex: 1, sortable: false },
      { headerName: 'Payment Status', field: 'paymentStatus', flex: 1, sortable: false },
      { headerName: 'Payment Timing', field: 'paymentTiming', flex: 1, sortable: false },
      { headerName: 'Tip Amount', field: 'tipAmount', flex: 1, sortable: false },
      { headerName: 'Credit Amount', field: 'creditAmount', flex: 1, sortable: false },
      { headerName: 'Promotion Amount', field: 'promotionAmount', flex: 1, sortable: false },
      { headerName: 'Balance Due', field: 'balanceDue', flex: 1, sortable: false },
    ];
    return (
      <CentsDataGridServer
        tableName="STORE_SERVICE_ORDERS"
        columns={SERVICE_ORDER_TABLE_COLUMNS}
        data={serviceOrders}
        rowCount={serviceOrderCount}
        pageSize={20}
        page={serviceOrdersPageNumber}
        onPageChange={({ page }) => setServiceOrdersPageNumber(page)}
        onRowClick={({ id }) => {
          navigateToIndividualServiceOrder(id);
        }}
        isLoading={isServiceOrdersTableLoading}
      />
    );
  };

  const createTableTabsData = () => {
    const tabsDataArray = [
      { name: 'Service Orders', value: 'serviceOrders' },
      { name: 'Inventory Orders', value: 'inventoryOrders' },
      { name: 'Payroc Terminal Accounts', value: 'payrocTerminalAccounts' },
      { name: 'PayPal Account', value: 'paypalAccount' },
      { name: 'Machine Uploads', value: 'machineUploads' },
      { name: 'Device Configuration', value: 'deviceConfiguration' },
    ];
    setTableTabsData(tabsDataArray);
  };

  /**
   * Navigate to an individual inventory order
   *
   * @param {Number} inventoryOrderId
   */
  const navigateToIndividualInventoryOrder = (inventoryOrderId) => {
    history.push(`/inventory-orders/${inventoryOrderId}`);
  };

  /**
   * Navigate to an individual service order
   *
   * @param {Number} serviceOrderId
   */
  const navigateToIndividualServiceOrder = (serviceOrderId) => {
    history.push(`/service-orders/${serviceOrderId}`);
  };

  const handleServiceOrderSearch = (searchInput) => {
    if (searchInput.length > 0) {
      if (searchInput !== serviceOrdersSearchString) {
        setServiceOrdersPageNumber(0);
      }
      setServiceOrdersSearchString(searchInput);
    } else {
      setServiceOrdersPageNumber(0);
      setServiceOrdersSearchString('');
    }
  };

  const handleInventoryOrderSearch = (searchInput) => {
    if (searchInput.length > 0) {
      if (searchInput !== inventoryOrdersSearchString) {
        setInventoryOrdersPageNumber(0);
      }
      setInventoryOrdersSearchString(searchInput);
    } else {
      setInventoryOrdersPageNumber(0);
      setInventoryOrdersSearchString('');
    }
  };

  const handleOfflineMachineFileSubmit = (file) => {
    const dataToUpload = {
      machineList: file,
      storeId: store.id,
    };
    uploadOfflineMachinesForStore(dataToUpload);
  };

  const renderUploadsSection = () => {
    return (
      <DocumentUpload title="Offline Machine Upload" onSave={handleOfflineMachineFileSubmit}>
        <div>
          <p>
            For uploading offline machines, please reference{' '}
            <a href={'/templates/offline-machine-upload-template.csv'} download>
              this CSV template
            </a>{' '}
            for accurate column names and formatting. Do not change the column names.
          </p>
          <p>
            For the machineType name, the acceptable values are either DRYER or WASHER (must be in
            all-caps). No other values will be accepted.
          </p>
          <p>
            Additionally, as seen in the template, please ensure the capacity column is a number
            (i.e., it should be 40, not "40 lbs").
          </p>
        </div>
      </DocumentUpload>
    );
  };

  return (
    <>
      {loading && <CentsLoader />}
      {!loading && (
        <>
          <div className="page-container">
            <div className="store-page-header-container">
              <div className="store-page-header">
                <div>Store Name: {store.name}</div>
                <div className="store-page-header-small-item">
                  <span>(id: </span>
                  <span className="font-italic">{store.id}</span>
                  <span>)</span>
                </div>
              </div>
            </div>
            <div>
              {toggleErrorMessage ? (
                <MuiAlert severity="error">{toggleErrorMessage}</MuiAlert>
              ) : null}
              <Tabs
                tabsData={tabsData}
                tabValueSelected={tabValueSelected}
                handleTabChange={handleTabChange}
              />
            </div>
            <div className="store-page--table-tab-container">
              <Tabs
                tabsData={tableTabsData}
                tabValueSelected={tableTabValueSelected}
                handleTabChange={handleTableTabChange}
              />
            </div>

            {tableTabValueSelected === TABLE_TAB_VALUES.serviceOrders && (
              <>
                <div className="store-page--order-search">
                  <TableSearchBar
                    value={serviceOrdersSearchString}
                    placeholder="Find a service order"
                    onSearch={handleServiceOrderSearch}
                  />
                </div>
                {serviceOrders.length ? (
                  createServiceOrdersTabsData()
                ) : (
                  <div className="store-page--nothing-to-display">No Service Orders to Display</div>
                )}
              </>
            )}

            {tableTabValueSelected === TABLE_TAB_VALUES.inventoryOrders && (
              <>
                <div className="store-page--order-search">
                  <TableSearchBar
                    value={inventoryOrdersSearchString}
                    placeholder="Find an inventory order"
                    onSearch={handleInventoryOrderSearch}
                  />
                </div>
                {inventoryOrders.length ? (
                  createInventoryOrdersTabsData()
                ) : (
                  <div className="store-page--nothing-to-display">
                    No Inventory Orders to Display
                  </div>
                )}
              </>
            )}

            {tableTabValueSelected === TABLE_TAB_VALUES.payrocTerminalAccounts && (
              <TerminalAccounts storeId={store.id} businessId={store.businessId} />
            )}

            {tableTabValueSelected === TABLE_TAB_VALUES.paypalAccount && (
              <PayPalAccount storeId={store.id} businessId={store.businessId} />
            )}

            {tableTabValueSelected === TABLE_TAB_VALUES.machineUploads && (
              <>
                {isUploadingOfflineMachinesForStore && <CentsLoader />}
                {!isUploadingOfflineMachinesForStore && renderUploadsSection()}
              </>
            )}
            {tableTabValueSelected === TABLE_TAB_VALUES.deviceConfiguration && (
              <StoreDeviceConfiguration
                envVariables={store.envVariables?.envVariables}
                defaultReleases={defaultReleases}
                storeId={store.id}
                onUpdateSuccess={() => retrieveStoreDetails({ showLoader: false })}
              />
            )}
          </div>

          {showModal && renderModal()}
          {showPasswordModal && renderPasswordModal()}
          {showSettingsModal && renderStoreSettingsModal()}
          {showPrinterSettingsModal && renderPrinterSettingsModal()}
          {showCentsDeliverySettingsModal && renderCentsDeliverySettingsModal()}
        </>
      )}
    </>
  );
};

IndividualStoreProfile.propTypes = {
  flags: PropTypes.object,
};

export default IndividualStoreProfile;
